export class UpdateLatch<I extends unknown[], T> {
	#updateFn: ( ...input: I ) => PromiseLike<T>;

	#dispatchFn: () => void;

	#lastInputKey: string | undefined;

	#updateGeneration: number;

	#pendingState: Promise<void> | null;

	#lastFullState: { success: boolean, data?: T, error?: Error } | null;

	#loading: boolean;

	constructor( updateFn: ( ...input: I ) => PromiseLike<T>, dispatchFn: () => void ) {
		this.#updateFn = updateFn;
		this.#dispatchFn = dispatchFn;
		this.#updateGeneration = 0;
		this.#lastInputKey = undefined;
		this.#pendingState = null;
		this.#lastFullState = null;
		this.#loading = false;
	}

	// Set the desired input for the input arguments for the state.
	// input must be JSON encodeable.
	setInput( ...input: I ): void {
		const inputKey = JSON.stringify( input );

		// IF input key matches: no update needed
		if ( this.#lastInputKey === inputKey ) {
			return;
		}
		this.#lastInputKey = inputKey;
		this.#loading = true;

		// ensure only the last update wins:
		// capture last update generation
		this.#updateGeneration += 1;
		const updateGeneration = this.#updateGeneration;

		setTimeout( () => {
			if ( this.#updateGeneration !== updateGeneration ) {
				// skip
				return;
			}

			const update = ( async() => {
				try {
					const data = await this.#updateFn( ...input );

					return {
						success: true,
						data: data,
					};
				} catch ( error ) {
					return {
						success: false,
						error: error,
					};
				}
			} )();

			const setState = ( async() => {
				// wait for data or error
				const result = await update;

				if ( this.#updateGeneration === updateGeneration ) {
					// If no other triggering setInput s were called
					this.#lastFullState = result;
					this.#loading = false;
					this.#dispatchFn();
				} else {
					// Otherwise steal result
					await this.#pendingState;
				}
			} )();

			this.#pendingState = setState;
		}, 25 );
	}

	// It the latch still loading?
	get loading(): boolean {
		return this.#loading;
	}

	// Get the last resolved state or null otherwise.
	// This function throws an error when the update function threw an error.
	state(): T | null {
		if ( null === this.#lastFullState ) {
			return null;
		}

		if ( this.#lastFullState.success ) {
			return this.#lastFullState.data!;
		}

		if ( !this.#lastFullState.error ) {
			throw new Error( 'invariant : unsuccessful states must have an error' );

		}

		throw this.#lastFullState.error;

	}
}
